import React, { useEffect, useRef, useState } from 'react'
import { useRequest } from '../../hooks/UseRequest'
import Loading from '../../components/Loading'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClose, faHistory } from '@fortawesome/free-solid-svg-icons'
import { useDispatch, useSelector } from 'react-redux'
import { selectModules } from '../../redux/moduleSlice'
import { selectDropdownOptions } from '../../redux/dropdownSlice'
import { setModal } from '../../redux/modalSlice'
import { generateTableKey } from '../../utils/listViewHelper'
import { DateFormat } from '../../helpers/formatting'

export default function HistoryDrawer() {
  const requests = useRequest()
  const inFlightPath = useRef('')
  const [row, setRow] = useState([])
  const [loaded, setLoaded] = useState(false)
  const modal = useSelector((state) => state.modal)
  const dispatch = useDispatch()
  const modules = useSelector(selectModules)
  const dropdownOptions = useSelector(selectDropdownOptions);
  const { title, content: endpointPath, type, is_active, parent_modal } = modal
  const module = modules.data.find((e) => e.code_key === parent_modal)
  const moduleFields = module?.module_fields
  const tableHeader = { id: 'ID#', field_name: 'Field', before_value_string: 'Old Value', after_value_string: 'New Value', created_by: 'Who', created_at: 'When' }

  const getRow = async (path) => {
    try {
      requests.setEndpoint(path)
      const response = await requests.get()
      // Ignore a response that a close or a newer request has superseded.
      if (inFlightPath.current !== path) return
      dataSerializing(response.data.data)
      setLoaded(true)
    } finally {
      if (inFlightPath.current === path) inFlightPath.current = ''
    }
  }

  const isValidDateTime = (str) => {
    // Regular expression for datetime format YYYY-MM-DD HH:MM:SS
    const regex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
    return regex.test(str);
  }

  const isValidDate = (str) => {
    // Regular expression for date format YYYY-MM-DD
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    return regex.test(str);
  }

  const renderData = (col, data, field_name) => {
    if (col === 'field_name') {
      return moduleFields?.find((e) => e.column_name === data)?.label_name || data;
    }
    if ((col === "before_value_string" || col === "after_value_string") &&
      (field_name.startsWith("is_") || field_name.startsWith("Is ") || field_name.startsWith("Payment on holidays"))) {
      // replace boolean values with yes and no
      if (data !== null) {
        return data === '1' ? 'Yes' : 'No';
      } else {
        return '';
      }
    } else if ((col === "before_value_string" || col === "after_value_string") && (moduleFields?.find((e) => e.column_name === field_name && e.dropdown_key))) {
      // replace numeric dropdown values with names
      return dropdownOptions.filter((o) => o.id == data).map((e) => e.name)[0]
    } else if (col === "created_at" || isValidDateTime(data)) {
      return <DateFormat dateString={data} formatString="MM/dd/yyyy hh:mm:ss a" />;
    } else if (isValidDate(data)) {
      return <DateFormat dateString={data} />
    } else {
      return data;
    }
  }

  useEffect(() => {
    if (type !== 'history' || endpointPath === '') {
      // Closing clears the endpoint; abandon any in-flight request so its
      // response can't land on the next record.
      inFlightPath.current = ''
      return
    }
    if (inFlightPath.current === endpointPath) return
    setLoaded(false)
    inFlightPath.current = endpointPath
    getRow(endpointPath)
  }, [endpointPath])

  const dataSerializing = (data) => {
    if (!data) return setRow([])
    //first, filter data with titleHeader
    const newData = data.map((row) => {
      const newRow = {}
      Object.keys(row).map((col) => {
        if (tableHeader[col] || col == 'is_deleted') {
          newRow[col] = row[col]
        }
      })
      return newRow
    })
    //second, sort by created_at
    newData.sort((a, b) => {
      return new Date(b.created_at) - new Date(a.created_at)
    })
    setRow(newData)
  }

  const slideUpDrawer = () => {
    dispatch(setModal({ is_active: false, type: '', title: title, content: '', options: {}, parent_modal: '' }))
  }
  const active = type === 'history' && is_active

  return (
    <div
      className={`lox-history-modal modal is-active is-justify-content-flex-start  ${active ? '' : 'is-invisible'} `}
    >
      <div className="modal-background"></div>
      <div
        className={`modal-card lox-slide-down-container ${active ? 'lox-slide-down' : 'lox-collapse'} `}
        style={{ width: '100%' }}
      >
        <section
          className="modal-card-body pb-0"
          style={{ maxHeight: '360px', overflow: 'hidden' }}
        >
          <p className="title has-text-primary is-flex mb-2 is-size-4">
            <FontAwesomeIcon icon={faHistory} className="is-size-5 pt-1" />{' '}
            &nbsp;{title}
          </p>
          {!loaded && (
            <div className="is-flex is-justify-content-center is-align-items-center" style={{ height: '295px' }}>
              <Loading loaded={loaded} />
            </div>
          )}
          {loaded && row.length === 0 && (
            <div className="columns">
              <div className="column is-flex is-justify-content-center is-align-items-center has-text-danger is-size-6 my-3" style={{ height: '295px' }}>
                <div>  - No History Found - </div>
              </div>
            </div>
          )}
          {loaded && row[0] && (
            <div className="columns my-2">
              <div
                className="lox-hooktable column is-full table-container m-0"
                style={{ height: '290px' }}
              >
                <table className="lox-hooktable-table table is-bordered is-striped is-narrow is-hoverable is-fullwidth is-grey">
                  <thead>
                    <tr className="is-selected">
                      {Object.keys(row[0]).filter((col) => col !== 'is_deleted').map((col, index) => {
                        return (
                          <th
                            key={generateTableKey(col, index)}
                            className="is-selected has-text-centered"
                            style={{ whiteSpace: 'nowrap' }}
                          >
                            {tableHeader[col] ? tableHeader[col] : col}
                          </th>
                        )
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {row.map((row, index) => {
                      return (
                        <tr key={generateTableKey(index)} className={row.is_deleted ? 'notification is-danger is-light' : ''}>
                          {Object.keys(row).filter((col) => col !== 'is_deleted').map((col) => {
                            return (
                              <td
                                key={generateTableKey(row[col])}
                                className="has-text-left"
                                style={{ whiteSpace: 'nowrap' }}
                              >
                                {renderData(col, row[col], row.field_name)}
                              </td>
                            )
                          })}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
        <footer className="modal-card-foot is-flex is-justify-content-flex-end py-2">
          <a
            className="lox-history-close-button has-background-grey has-text-white is-medium px-2"
            onClick={slideUpDrawer}
          >
            <FontAwesomeIcon icon={faClose} /> Close
          </a>
        </footer>
      </div>
    </div>
  )
}
